/// <reference types="google.maps" />

import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AddressPrefix } from '@app/shared/enums';
import { GoogleMapsLoaderService } from './google-maps-loader.service';

export interface AddressAutocompleteOption {
  description: string;
  placeId: string;
}

export interface ParsedAddress {
  city?: string;
  state?: string;
  street1?: string;
  street2?: string;
  zip?: string;
}

@Injectable({ providedIn: 'root' })
export class GooglePlacesAddressService {
  private autocompleteService?: google.maps.places.AutocompleteService;
  private placesLibrary?: Promise<google.maps.PlacesLibrary>;
  private placesService?: google.maps.places.PlacesService;

  private mapsLoader = inject(GoogleMapsLoaderService);
  private platformId = inject(PLATFORM_ID);

  public async createAddressAutocomplete(
    input: HTMLInputElement,
    country: string | string[] = 'us',
  ): Promise<google.maps.places.Autocomplete | undefined> {
    const placesLibrary = await this.loadPlacesLibrary();

    if (!placesLibrary) {
      return undefined;
    }

    input.setAttribute('autocomplete', 'off');

    return new placesLibrary.Autocomplete(input, {
      componentRestrictions: { country },
      fields: ['address_components', 'formatted_address', 'geometry'],
      types: ['address'],
    });
  }

  public async getAddressPredictions(
    input: string,
    country: string | string[] = 'us',
  ): Promise<AddressAutocompleteOption[]> {
    const autocompleteService = await this.ensureAutocompleteService();

    if (!autocompleteService || input.trim().length < 3) {
      return [];
    }

    return new Promise((resolve) => {
      autocompleteService.getPlacePredictions(
        {
          componentRestrictions: { country },
          input,
          types: ['address'],
        },
        (
          predictions: google.maps.places.AutocompletePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus,
        ) => {
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          ) {
            resolve([]);
            return;
          }

          resolve(
            predictions.map((prediction) => ({
              description: prediction.description,
              placeId: prediction.place_id,
            })),
          );
        },
      );
    });
  }

  public async getParsedAddressByPlaceId(
    placeId: string,
  ): Promise<ParsedAddress | null> {
    const placesService = await this.ensurePlacesService();

    if (!placesService) {
      return null;
    }

    return new Promise((resolve) => {
      placesService.getDetails(
        {
          fields: ['address_components'],
          placeId,
        },
        (
          place: google.maps.places.PlaceResult | null,
          status: google.maps.places.PlacesServiceStatus,
        ) => {
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !place?.address_components
          ) {
            resolve(null);
            return;
          }

          resolve(this.parseAddressComponents(place.address_components));
        },
      );
    });
  }

  public parseAddressComponents(
    components: google.maps.GeocoderAddressComponent[],
  ): ParsedAddress {
    const byType = (type: string) =>
      components.find((component) => component.types.includes(type));
    const getShort = (type: string) => byType(type)?.short_name ?? '';
    const getLong = (type: string) => byType(type)?.long_name ?? '';

    const streetNumber = getShort('street_number');
    const route = getLong('route');
    const subpremise = getShort('subpremise');
    const locality =
      getLong('locality') ||
      getLong('postal_town') ||
      getLong('sublocality') ||
      '';
    const state = getShort('administrative_area_level_1');
    const postal = getShort('postal_code');
    const postalSuffix = getShort('postal_code_suffix');
    const street1 = [streetNumber, route].filter(Boolean).join(' ').trim();
    const zip = postalSuffix ? `${postal}`.slice(0, 5) : postal;

    return {
      city: locality || undefined,
      state: state || undefined,
      street1: street1 || undefined,
      street2: subpremise || undefined,
      zip: zip || undefined,
    };
  }

  public patchAddressForm(
    formGroup: FormGroup,
    prefix: AddressPrefix,
    parsedAddress: ParsedAddress,
  ): void {
    const key = (suffix: string) => `${prefix}${suffix}`;

    if (prefix === AddressPrefix.Mailing || prefix === AddressPrefix.Physical) {
      formGroup.patchValue(
        {
          [key('Street1')]: parsedAddress.street1 ?? '',
          [key('Street2')]:
            parsedAddress.street2 ?? formGroup.get(key('Street2'))?.value ?? '',
          [key('City')]: parsedAddress.city ?? '',
          [key('State')]: parsedAddress.state ?? '',
          [key('Zip')]: parsedAddress.zip ?? '',
        },
        { emitEvent: false },
      );
    } else {
      formGroup.patchValue(
        {
          [key('StAddr')]: parsedAddress.street1 ?? '',
          [key('City')]: parsedAddress.city ?? '',
          [key('State')]: parsedAddress.state ?? '',
          [key('ZipCode')]: parsedAddress.zip ?? '',
        },
        { emitEvent: false },
      );
    }

    formGroup.markAsDirty();
  }

  private async ensureAutocompleteService(): Promise<
    google.maps.places.AutocompleteService | undefined
  > {
    const placesLibrary = await this.loadPlacesLibrary();

    if (!placesLibrary) {
      return undefined;
    }

    this.autocompleteService ??= new placesLibrary.AutocompleteService();

    return this.autocompleteService;
  }

  private async ensurePlacesService(): Promise<
    google.maps.places.PlacesService | undefined
  > {
    const placesLibrary = await this.loadPlacesLibrary();

    if (!placesLibrary) {
      return undefined;
    }

    this.placesService ??= new placesLibrary.PlacesService(
      document.createElement('div'),
    );

    return this.placesService;
  }

  private async loadPlacesLibrary(): Promise<
    google.maps.PlacesLibrary | undefined
  > {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }

    await this.mapsLoader.loadPlaces();
    this.placesLibrary ??= google.maps.importLibrary(
      'places',
    ) as Promise<google.maps.PlacesLibrary>;

    return this.placesLibrary;
  }
}
